import { useCallback, useEffect, useRef, useState } from 'react'
import {
  Save,
  Undo2,
  Redo2,
  MousePointer2,
  Plus,
  Spline,
  QrCode,
  MapPin,
  PenSquare,
  PackageCheck,
} from 'lucide-react'
import clsx from 'clsx'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { TOOL, EDITOR_MODE, useSpatialStore } from '@/features/spatial-model/store.js'
import { saveFloorGraph } from '@/lib/api/spaces.js'
import { publishFloor } from '@/lib/api/publish.js'

const AUTOSAVE_INTERVAL_MS = 60_000

function formatTime(date) {
  if (!date) return null
  try {
    return date.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit', second: '2-digit' })
  } catch {
    return date.toISOString()
  }
}

const TOOL_BUTTONS = [
  { tool: TOOL.SELECT, icon: MousePointer2, label: 'Select' },
  { tool: TOOL.ADD_NODE, icon: Plus, label: 'Add node' },
  { tool: TOOL.ADD_EDGE, icon: Spline, label: 'Connect edge' },
  { tool: TOOL.ADD_ANCHOR, icon: QrCode, label: 'Add anchor' },
  { tool: TOOL.ADD_DESTINATION, icon: MapPin, label: 'Add destination' },
  { tool: TOOL.ADD_ZONE, icon: PenSquare, label: 'Draw zone' },
]

const MODE_TABS = [
  { mode: EDITOR_MODE.TWO_D, label: '2D' },
  { mode: EDITOR_MODE.THREE_D, label: '3D' },
  { mode: EDITOR_MODE.SPLIT, label: 'Split' },
  { mode: EDITOR_MODE.AR_PREVIEW, label: 'AR' },
]

export default function WorkspaceToolbar({ disabled }) {
  const tool = useSpatialStore((s) => s.tool)
  const mode = useSpatialStore((s) => s.mode)
  const setTool = useSpatialStore((s) => s.setTool)
  const setMode = useSpatialStore((s) => s.setMode)
  const dirty = useSpatialStore((s) => s.dirty)
  const undo = useSpatialStore((s) => s.undo)
  const redo = useSpatialStore((s) => s.redo)
  const graph = useSpatialStore((s) => s.graph)
  const floor = useSpatialStore((s) => s.floor)
  const validation = useSpatialStore((s) => s.validation)
  const markClean = useSpatialStore((s) => s.markClean)
  const queryClient = useQueryClient()
  const [lastSavedAt, setLastSavedAt] = useState(null)
  const [saveError, setSaveError] = useState(null)

  const saveMutation = useMutation({
    mutationFn: ({ floorId, snapshot }) => saveFloorGraph(floorId, snapshot),
    onSuccess: () => {
      markClean()
      setLastSavedAt(new Date())
      setSaveError(null)
      queryClient.invalidateQueries({ queryKey: ['orgs'] })
      if (floor?.id) {
        queryClient.invalidateQueries({ queryKey: ['floor', floor.id, 'graph'] })
      }
    },
    onError: (err) => {
      const detail =
        err?.response?.data?.message ||
        err?.response?.data?.detail ||
        err?.uiMessage ||
        err?.message ||
        'Save failed'
      setSaveError(detail)
    },
  })

  const triggerSave = useCallback(() => {
    if (!floor?.id) return
    if (saveMutation.isPending) return
    saveMutation.mutate({ floorId: floor.id, snapshot: graph })
  }, [floor?.id, graph, saveMutation])

  const triggerSaveRef = useRef(triggerSave)
  useEffect(() => {
    triggerSaveRef.current = triggerSave
  }, [triggerSave])

  const dirtyRef = useRef(dirty)
  useEffect(() => {
    dirtyRef.current = dirty
  }, [dirty])

  useEffect(() => {
    if (!floor?.id) return undefined
    const id = setInterval(() => {
      if (dirtyRef.current) triggerSaveRef.current?.()
    }, AUTOSAVE_INTERVAL_MS)
    return () => clearInterval(id)
  }, [floor?.id])

  const publishMutation = useMutation({
    mutationFn: () => publishFloor(floor.id, { change_summary: `Published from admin at ${new Date().toISOString()}` }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['floor', floor.id] })
    },
  })

  const blocked = (validation.blockers?.length || 0) > 0

  return (
    <div className="workspace__toolbar">
      <button
        className="btn btn--ghost btn--sm"
        type="button"
        disabled={disabled || !floor || saveMutation.isPending}
        onClick={triggerSave}
        title={
          saveError
            ? `Last save failed: ${saveError}`
            : lastSavedAt
              ? `Last saved at ${formatTime(lastSavedAt)}`
              : 'Save draft'
        }
      >
        <Save size={14} />{' '}
        {saveMutation.isPending
          ? 'Saving…'
          : saveError
            ? 'Retry save'
            : dirty
              ? 'Save draft *'
              : lastSavedAt
                ? `Saved ${formatTime(lastSavedAt)}`
                : 'Save draft'}
      </button>
      <button className="btn btn--ghost btn--sm" type="button" onClick={undo}>
        <Undo2 size={14} />
      </button>
      <button className="btn btn--ghost btn--sm" type="button" onClick={redo}>
        <Redo2 size={14} />
      </button>
      <div className="divider" />
      {TOOL_BUTTONS.map((t) => (
        <button
          key={t.tool}
          type="button"
          className={clsx('btn btn--ghost btn--sm', tool === t.tool && 'btn--primary')}
          onClick={() => setTool(t.tool)}
          title={t.label}
          disabled={disabled}
        >
          <t.icon size={14} /> {t.label}
        </button>
      ))}
      <div style={{ flex: 1 }} />
      <div className="mode-tabs" style={{ marginRight: 8 }}>
        {MODE_TABS.map((m) => (
          <button
            key={m.mode}
            type="button"
            className={clsx('mode-tab', mode === m.mode && 'is-active')}
            onClick={() => setMode(m.mode)}
          >
            {m.label}
          </button>
        ))}
      </div>
      <button
        className="btn btn--primary btn--sm"
        type="button"
        title={blocked ? 'Resolve validation blockers first' : 'Publish runtime package'}
        disabled={!floor || blocked || publishMutation.isPending}
        onClick={() => publishMutation.mutate()}
      >
        <PackageCheck size={14} /> {publishMutation.isPending ? 'Publishing…' : 'Publish'}
      </button>
    </div>
  )
}
